import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { createAPIRequest, getContextId } from '@tlx/astro-shared';
import { Button } from '@tlx/atlas';
import {
    BannerButtons,
    BannerDescription,
    BannerImage,
    BannerPersistence,
    BannerTitle,
    InfoLabel,
    MinimizableBanner,
    useGetStartedBanner,
} from '@tlx/beehive-shared';
import { APIUrls } from '@Util/urls';

const BANNER_ID = 'Altinn3Banner';
const HELP_ARTICLE_URL =
    'https://hjelp.tripletex.no/hc/no/articles/45252620100497-Slik-oppretter-du-kobling-mot-Altinn-3';
const HELP_ARTICLE_URL_ACCOUNTANT =
    'https://hjelp.tripletex.no/hc/no/articles/43062518335761-Slik-oppretter-du-kobling-mot-Altinn-3-For-regnskapsbyr%C3%A5';

export function OptInAltinn3Banner() {
    const { formatMessage } = useIntl();
    const [bannerState, setBannerState] = useState<{
        shouldHide: boolean;
        accountantOrSimilar: boolean;
    }>({ shouldHide: true, accountantOrSimilar: false });

    useEffect(() => {
        getBannerState().then(setBannerState);
    }, []);

    const onActivate = useCallback(async () => {
        const request = createAPIRequest('/v2/pilotFeature/activateAltinn3', {
            method: 'PUT',
        });
        try {
            const response = await fetch(request);
            if (!response.ok) {
                window.alert(formatMessage({ id: 'heading_unexpected_error' }));
                return;
            }
            window.location.replace(`/execute/wageSettingsMenu?contextId=${getContextId()}&scrollTo=altinn-section`);
        } catch {
            window.alert(formatMessage({ id: 'heading_unexpected_error' }));
        }
    }, [formatMessage]);

    const bannerInfo = useGetStartedBanner(BANNER_ID, BannerPersistence.Permanent);

    if (bannerState.shouldHide || !bannerInfo) {
        return null;
    }

    const descriptionKey = bannerState.accountantOrSimilar
        ? 'altinn3_activation_banner_description_accountant'
        : 'altinn3_activation_banner_description';
    const helpArticleUrl = bannerState.accountantOrSimilar ? HELP_ARTICLE_URL_ACCOUNTANT : HELP_ARTICLE_URL;

    return (
        <div className="atl-mb-16">
            <MinimizableBanner use={bannerInfo}>
                <BannerImage src="imgs/rocket.svg" alt="rocket" />
                <InfoLabel />
                <BannerTitle>
                    {formatMessage({
                        id: 'altinn3_activation_banner_title',
                    })}
                </BannerTitle>
                <BannerDescription>
                    {formatMessage({
                        id: descriptionKey,
                    })}
                </BannerDescription>
                <BannerButtons>
                    <Button
                        data-testid="altinn3-banner-activate"
                        data-trackingid="get-started-banner-link__Altinn3Banner__activate"
                        variant="primary"
                        onClick={onActivate}
                    >
                        {formatMessage({
                            id: 'altinn3_activation_banner_button_activate',
                        })}
                    </Button>
                    <Button
                        data-testid="altinn3-banner-read-more"
                        data-trackingid="get-started-banner-link__Altinn3Banner__read-more"
                        variant="secondary"
                        onClick={() => window.open(helpArticleUrl, '_blank')}
                    >
                        {formatMessage({
                            id: 'text_read_more',
                        })}
                    </Button>
                </BannerButtons>
            </MinimizableBanner>
        </div>
    );
}

async function getBannerState(): Promise<{
    shouldHide: boolean;
    accountantOrSimilar: boolean;
}> {
    try {
        const [pilotResponse, eligibleResponse] = await Promise.all([
            fetch(createAPIRequest(`${APIUrls.PILOT_FEATURE}?pilotFeatures=Altinn3`, { method: 'GET' })),
            fetch(
                createAPIRequest(APIUrls.ALTINN3_BANNER_ELIGIBLE, {
                    method: 'GET',
                }),
            ),
        ]);
        const pilotData = await pilotResponse.json();
        const eligibleData = await eligibleResponse.json();
        const shouldHide = pilotData.value['Altinn3'] || !eligibleData.value.eligible;
        return {
            shouldHide,
            accountantOrSimilar: !!eligibleData.value.accountantOrSimilar,
        };
    } catch {
        return { shouldHide: true, accountantOrSimilar: false };
    }
}
